import { ACCOUNT_REFERRAL_TYPE } from '../constants/account-referrals';

export const DISCOUNT_MODE = {
  AMOUNT: 'AMOUNT',
  PERCENTAGE: 'PERCENTAGE',
};

export const DISCOUNT_DURATION = {
  ONCE: 'ONCE',
  FOREVER: 'FOREVER',
  REPEATING: 'REPEATING',
};

export const USAGE_LIMIT_MODE = {
  GLOBAL: 'GLOBAL',
  ACCOUNT: 'ACCOUNT',
};

export const PROMO_CODE_SCOPE = {
  ALBUM_FOLDER: 'ALBUM_FOLDER',
  ACCOUNT: 'ACCOUNT',
  GLOBAL: 'GLOBAL',
};

/**
 * Return the discount price using an AccountPromoCode.
 *
 * @param {Number} price The item price.
 * @param {Object} accountPromoCode The AccountPromoCode to apply.
 *
 * @returns {Number} The discount price.
 */
export const getDiscountPrice = (price, accountPromoCode) => {
  if (!accountPromoCode?.discount) {
    return price;
  }
  if (accountPromoCode.discount.mode === DISCOUNT_MODE.AMOUNT) {
    return price - accountPromoCode.discount.value;
  }

  return Math.round(price * ((100 - accountPromoCode.discount.value) / 100));
};

/**
 * Whether a promo code makes a Digital Photo Pass free: a SUBSCRIPTIONS code
 * with a 100% discount that has not expired.
 *
 * @param {Object} promoCode The PromoCode / AccountPromoCode record.
 * @param {Date} [now] The reference date, for tests.
 *
 * @returns {Boolean} Whether the code gives a free pass.
 */
export const isFreeSubscriptionPromoCode = (promoCode, now = new Date()) => {
  if (
    !promoCode?.code ||
    promoCode.type !== ACCOUNT_REFERRAL_TYPE.SUBSCRIPTIONS
  ) {
    return false;
  }

  if (
    promoCode.discount?.mode !== DISCOUNT_MODE.PERCENTAGE ||
    promoCode.discount?.value !== 100
  ) {
    return false;
  }

  if (promoCode.expires_at && new Date(promoCode.expires_at) <= now) {
    return false;
  }

  return true;
};

/**
 * Picks the first promo code that makes a Digital Photo Pass free.
 *
 * @param {Array<Object>} promoCodes The PromoCode records.
 * @param {Date} [now] The reference date, for tests.
 *
 * @returns {Object|null} The promo code, or null when none qualifies.
 */
export const pickFreeSubscriptionPromoCode = (promoCodes, now = new Date()) =>
  (promoCodes ?? []).find(promoCode =>
    isFreeSubscriptionPromoCode(promoCode, now),
  ) ?? null;
